/**
 * Services — data layer. Verbatim port of the canonical data-services.jsx.
 *
 * Three entities, one per Services tab:
 *   Service  — a reusable, bookable offering (events / appointments / queues)
 *   Category — an ordered grouping of services (reorder on its tab)
 *   Package  — a bundle of categories AND/OR individual services
 *
 * Admin-authored. Persistence: REQ-004 (CLAUDE-CODE-REQUESTS.md) is live —
 * this store hydrates from and persists through the real `/v1/admin/catalog/*`
 * client (briefings/services.md). An in-memory seed is kept only as a
 * same-session fallback for previews where the client isn't wired yet.
 * Every entity is Published / Draft — no approval gate.
 *
 * Ordering: `state.services` array order is canonical; within a category we
 * filter preserving that order.
 */
import { useEffect, useReducer } from "react";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, isSignedIn, onTenantChange, onTenantSwitching } from "../../shell/access";
import { t as i18nT } from "../../lib/i18n";
import {
  listStudioServices, createStudioService, updateStudioService, deleteStudioService, duplicateStudioService, moveStudioService,
  listServiceCategories, createServiceCategory, updateServiceCategory, deleteServiceCategory, duplicateServiceCategory, reorderServiceCategories,
  listStudioPackages, createStudioPackage, updateStudioPackage, deleteStudioPackage, duplicateStudioPackage, reorderStudioPackages,
  presignStudioServiceImage, uploadStudioServiceImage, confirmStudioServiceImage,
  type StudioServicesRequestOptions,
} from "@v0y4ge/api-client";

// baseUrl-carrying options required by the services list + package choke-points
// (packet #3 RC1). MSW intercepts by path in design-time, so a placeholder base
// is fine here; the real base is wired on deploy.
const REQUEST_OPTS: StudioServicesRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
};

/* Admin service library, every page. The admin list defaults to 25 rows and
 * caps a page at 100, so a single call silently truncated the library and the
 * All / Published / Drafts counts, statuses and categories only described the
 * first page. Reads through the typed admin operation (never the public
 * journey catalogue, which omits drafts) until the server reports no more.
 * Throws when a page comes back empty while more are promised, so hydrate()
 * keeps its current state instead of rendering a short list as the whole. */
const SERVICES_PAGE_LIMIT = 100;
export async function listAllStudioServices(
  opts: StudioServicesRequestOptions = REQUEST_OPTS,
  list: typeof listStudioServices = listStudioServices,
): Promise<Service[]> {
  const seen = new Map<string, Service>();
  let page = 1;
  // Ceiling so a server reporting a moving total cannot loop forever: one
  // page beyond what the first total implied. Sized from the page length the
  // server says it applied (`limit`), not the one requested -- a server that
  // caps below SERVICES_PAGE_LIMIT needs more pages than the request implies,
  // and a ceiling from the requested size refused a complete library.
  let maxPages = Infinity;
  for (;;) {
    const res = await list({ page: String(page), limit: String(SERVICES_PAGE_LIMIT) }, opts);
    const rows = ((res as any).services ?? (res as any).items ?? []) as Service[];
    if (maxPages === Infinity) {
      const served = Number(res.limit);
      const pageSize = served > 0 ? served : Math.max(rows.length, 1);
      maxPages = Math.ceil((Number(res.total) || 0) / pageSize) + 1;
    }
    for (const row of rows) seen.set(row.id, row);
    if (!res.hasMore) break;
    if (rows.length === 0 || page >= maxPages) {
      throw new Error(`Services pagination incomplete: ${seen.size} of ${res.total} after page ${page}`);
    }
    page += 1;
  }
  return Array.from(seen.values());
}

export type SvcChannelId = "events" | "appointments" | "queues";
export interface SvcChannelDef { id: SvcChannelId; label: string; labelKey: string; icon: string; hue: string; }
export const SVC_CHANNELS: SvcChannelDef[] = [
  { id: "events",       labelKey: "services.data.events", get label() { return i18nT("services.data.events"); },       icon: "calendar", hue: "#5142C7" },
  { id: "appointments", labelKey: "services.data.appointments", get label() { return i18nT("services.data.appointments"); }, icon: "clock",    hue: "#1F8A5B" },
  { id: "queues",       labelKey: "services.data.queues", get label() { return i18nT("services.data.queues"); },       icon: "list",     hue: "#C77F14" },
];
export const SVC_CHANNEL = (id: string): SvcChannelDef => SVC_CHANNELS.find((c) => c.id === id) || SVC_CHANNELS[0];

export const SVC_MODES: Record<string, { label: string; labelKey: string; tone: string }> = {
  "in-person": { labelKey: "services.data.inPerson", get label() { return i18nT("services.data.inPerson"); }, tone: "neutral" },
  "virtual":   { labelKey: "services.data.virtual", get label() { return i18nT("services.data.virtual"); },   tone: "info" },
  "hybrid":    { labelKey: "services.data.hybrid", get label() { return i18nT("services.data.hybrid"); },    tone: "primary" },
};

/* Delivery methods — a service may support several; each carries its own
 * settings (duration, capacity, schedule, pricing, questions, comms, …). */
export interface SvcMethodDef { id: string; label: string; labelKey: string; sub: string; icon: string; hue: string; }
export const SVC_METHODS: SvcMethodDef[] = [
  { id: "in-person",         labelKey: "services.data.inPerson", get label() { return i18nT("services.data.inPerson"); },         sub: "Store / branch",   icon: "pin",    hue: "#5142C7" },
  { id: "online",            labelKey: "services.data.onlineMeeting", get label() { return i18nT("services.data.onlineMeeting"); },    sub: "Video",            icon: "video",  hue: "#1F8A5B" },
  { id: "live-chat",         labelKey: "services.data.liveTextChat", get label() { return i18nT("services.data.liveTextChat"); },    sub: "Real-time chat",   icon: "msg",    hue: "#0E7490" },
  { id: "live-screen",       labelKey: "services.data.liveScreenShare", get label() { return i18nT("services.data.liveScreenShare"); }, sub: "Co-browse / demo", icon: "layers", hue: "#8955F2" },
  { id: "phone",             labelKey: "services.data.phone", get label() { return i18nT("services.data.phone"); },             sub: "Phone call",       icon: "phone",  hue: "#2D60EB" },
  { id: "email",             labelKey: "services.data.email", get label() { return i18nT("services.data.email"); },             sub: "Asynchronous",     icon: "mail",   hue: "#B5791F" },
  { id: "customer-location", labelKey: "services.data.customerLocation", get label() { return i18nT("services.data.customerLocation"); }, sub: "Home / business",  icon: "loc",    hue: "#C13584" },
];
export const SVC_METHOD = (id: string): SvcMethodDef => SVC_METHODS.find((m) => m.id === id) || SVC_METHODS[0];
export const PAYMENT_TIMINGS = [{ id: "booking", labelKey: "services.data.atBooking", get label() { return i18nT("services.data.atBooking"); } }, { id: "visit", labelKey: "services.data.atTheVisit", get label() { return i18nT("services.data.atTheVisit"); } }, { id: "none", labelKey: "services.data.noPayment", get label() { return i18nT("services.data.noPayment"); } }];
export const SVC_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const SVC_AUDIENCES = [{ id: "everyone", labelKey: "services.data.everyone", get label() { return i18nT("services.data.everyone"); } }, { id: "members", labelKey: "services.data.membersOnly", get label() { return i18nT("services.data.membersOnly"); } }, { id: "vip", labelKey: "services.data.vipOnly", get label() { return i18nT("services.data.vipOnly"); } }, { id: "new", labelKey: "services.data.newCustomers", get label() { return i18nT("services.data.newCustomers"); } }];

export const _svcSlug = (s?: string) => (s || "service").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "service";

export type SvcMethodConfig = Record<string, any>;
export type Service = Record<string, any>;
export type SvcCategory = { id: string; name: string; description: string; hue: string; status: string; updated: string };
export type SvcPackageItem = { type: "category" | "service"; id: string };
export type SvcPackage = { id: string; name: string; description: string; status: string; hue: string; items: SvcPackageItem[]; updated: string };

/* Per-method config factory. Defaults a sensible weekly availability. */
export function svcDefaultMethodConfig(methodId: string, base?: { duration?: number; capacity?: number }): SvcMethodConfig {
  const wk: Record<string, { start: string; end: string }[]> = {};
  SVC_WEEKDAYS.forEach((d) => (wk[d] = (d === "Sat" || d === "Sun") ? [] : [{ start: "09:00", end: "17:00" }]));
  return {
    duration: (base && base.duration) || 30, pre: 0, post: 0, capacity: (base && base.capacity) || 1,
    pricing: { price: 0, deposit: 0, tax: 0, cancelFee: 0, noShowFee: 0, paymentTiming: "booking" },
    schedule: wk,
    questions: [],        // [{ kind:'group'|'question', id }]
    promos: [],           // [discountId]
    comms: { mode: "flow", flowId: "", custom: [] },
    // method-specific
    room: "", locations: [],                                  // in-person
    provider: "zoom", autoLink: true, joinInstructions: "",   // online
    whoCalls: "staff",                                        // phone
    sla: "1 business day", deliverable: "",                   // email
    driveTime: 30, travelRadius: 15, travelFee: 0, captureAddress: true, // customer-location
  };
}

/* Derive the enabled delivery methods from a legacy seed's `mode`. */
export function svcMethodsFromMode(mode?: string): string[] {
  if (mode === "virtual") return ["online"];
  if (mode === "hybrid") return ["in-person", "online"];
  return ["in-person"];
}

/* Normalize any service record to the full editable shape (fills defaults so
 * seeds open cleanly in the editor). Non-destructive. */
export function ensureServiceShape(s: Service): Service {
  /* A server row names the length `durationMinutes`; the editor's summary field
     and svcDefaultMethodConfig's own default both read `duration`. Without this,
     a 45-minute service read back with no `duration` builds every method default
     at 30 — the helper's `|| 30` fallback — so the editor shows 30 and the next
     save writes 30 back.

     Fills a MISSING value only: an edited `duration` is never overwritten, and
     because the loop below spreads a stored config OVER the default
     (`{ ...svcDefaultMethodConfig(m, s), ...(methodConfig[m] || {}) }`), an
     existing rich methodConfig is left exactly as it was. Rebinds to a COPY, so
     the caller's object is not mutated. */
  const uiDuration = Number(s.duration);
  if (!(Number.isFinite(uiDuration) && uiDuration > 0)) {
    const serverMinutes = Number(s.durationMinutes);
    if (Number.isFinite(serverMinutes) && serverMinutes > 0) s = { ...s, duration: serverMinutes };
  }
  const methods: string[] = (s.methods && s.methods.length) ? s.methods : svcMethodsFromMode(s.mode);
  const methodConfig: Record<string, SvcMethodConfig> = { ...(s.methodConfig || {}) };
  methods.forEach((m) => { methodConfig[m] = { ...svcDefaultMethodConfig(m, s), ...(methodConfig[m] || {}) }; });
  const imageAssetId = serviceImageAssetIdOf(s);
  return {
    channels: ["appointments"], staff: 1, bookings: 0,
    ...s,
    methods,
    methodConfig,
    visibility: s.visibility || "public",
    confirmation: s.confirmation || "auto",            // 'auto' | 'review'
    /* A stored `blob:` hero is dropped on load (CD docket 2026-09-15). Records
     * saved before the upload fix carry an object URL that only ever resolved
     * in the tab that minted it; clearing it here shows the placeholder rather
     * than a broken image, and stops the dead value being re-saved. Widened for
     * the service-image asset lifecycle: a `data:` hero is not durable either,
     * so anything that is not a stored URL is dropped the same way. */
    landing: s.landing
      ? (s.landing.hero && typeof s.landing.hero.image === "string" && s.landing.hero.image && !durableServiceImageUrl(s.landing.hero.image)
        ? { ...s.landing, hero: { ...s.landing.hero, image: "" } }
        : s.landing)
      : { slug: _svcSlug(s.name), hero: { headline: s.name || "", blurb: "", image: "" } },
    /* The confirmed asset behind the image, or null for a pasted URL / no
       image. Never survives without a durable image beside it. Written in BOTH
       spellings so a record stored under either one is cleared, not left with a
       stale id pointing at an image this service no longer shows. */
    imageAssetId,
    image_asset_id: imageAssetId,
    staffRoles: s.staffRoles || [],
    staffGroups: s.staffGroups || [],
    staffing: s.staffing || [],           // [{ groupId, count }] — staff needed (optional)
    resourcing: s.resourcing || [],       // [{ groupId, count }] — resources needed (optional)
    resources: s.resources || [],
    concurrency: s.concurrency || 1,
    locScope: s.locScope || [],
    audience: s.audience || "everyone",
    addons: s.addons || [],
    prereqs: s.prereqs || [],
    tags: s.tags || [],
    exceptions: s.exceptions || [],       // [{ kind, label, from, to }]
    feedback: !!s.feedback,
    /* requiresPerson (kit delta 2026-09-11, JAI-1292).
       TRUE means availability comes from the assigned team's working hours.
       FALSE means hours attached to the service itself — and such a service is
       NOT offered on the public booking page.

       Defaulted here rather than left undefined, for two reasons. The backend
       defaults an omitted value to true on CREATE only, so an existing service
       read back without the field would flip meaning if we treated absent as
       false. And the toggle needs a definite value to render; `checked={undefined}`
       is an uncontrolled input. Non-appointment channels have no person to
       require, so they are false. */
    requiresPerson: typeof s.requiresPerson === "boolean"
      ? s.requiresPerson
      : (s.channels || ["appointments"]).includes("appointments"),
  };
}

/* Recompute the summary fields the list views read, from the rich shape. */
export function svcSyncSummary(s: Service): Service {
  const methods: string[] = s.methods || [];
  const remote = methods.every((m) => m === "online" || m === "phone" || m === "email" || m === "live-chat" || m === "live-screen");
  const physical = methods.every((m) => m === "in-person" || m === "customer-location");
  s.mode = methods.length === 0 ? (s.mode || "in-person") : (remote ? "virtual" : physical ? "in-person" : "hybrid");
  const first = methods[0] && s.methodConfig && s.methodConfig[methods[0]];
  if (first) { s.duration = first.duration; s.capacity = first.capacity; }
  return s;
}

let _svcSeq = 300;
const _svcId = (p: string) => `${p}_${(++_svcSeq).toString(36)}${Date.now().toString(36).slice(-3)}`;




/* ── Reactive store ─────────────────────────────────────────────────────── */
/**
 * 2026-07-03 (Claude Code push-back, §2): REQ-004 is live — full CRUD +
 * moveService/reorderCategory over `/v1/admin/catalog/*` (briefings/services.md).
 * This store now hydrates from + persists through that client instead of
 * localStorage. The in-memory seed below is kept ONLY as a same-session
 * fallback for when the client operations aren't present (this authoring
 * preview's fixture stub doesn't implement them yet) — it is never written
 * back to localStorage, and a successful hydrate() fully replaces it.
 */
interface SvcState { services: Service[]; categories: SvcCategory[]; packages: SvcPackage[]; }
let state: SvcState = {
  services:   [],
  categories: [],
  packages:   [],
};
const subs = new Set<() => void>();
const emit = () => { subs.forEach((fn) => fn()); };

function catFromDto(c: Record<string, any>): SvcCategory {
  return { id: c.id ?? c.categoryId, name: c.name ?? "", description: c.description ?? "", hue: c.hue ?? "#5142C7", status: c.status ?? "published", updated: c.updated ?? c.updatedAt ?? "" };
}
function pkgFromDto(p: Record<string, any>): SvcPackage {
  return { id: p.id ?? p.packageId, name: p.name ?? "", description: p.description ?? "", status: p.status ?? "published", hue: p.hue ?? "#5142C7", items: p.items ?? [], updated: p.updated ?? p.updatedAt ?? "" };
}

let _hydrated = false;
async function hydrate() {
  // Boot hygiene (2026-07-19 packet §2): this module is imported by screens.tsx
  // the instant the bundle loads — never fetch before a session exists. A
  // signed-out skip does NOT burn the flag; subscribe() re-invokes post-sign-in.
  if (_hydrated || !isSignedIn()) return;
  _hydrated = true;
  try {
    const [sr, cr, pr] = await Promise.all([
      typeof listStudioServices === "function" ? listAllStudioServices() : Promise.resolve(null),
      typeof listServiceCategories === "function" ? listServiceCategories(REQUEST_OPTS) : Promise.resolve(null),
      typeof listStudioPackages === "function" ? listStudioPackages({}, REQUEST_OPTS) : Promise.resolve(null),
    ]);
    // Only overwrite each entity when the server actually returned rows —
    // an empty envelope (e.g. this preview harness's fixture stub) must not
    // stomp the in-memory seed, same guard as cal-categories.ts/integration-configs.ts.
    const srArr: Service[] = sr ?? [];
    const crArr = cr ? (((cr as any).categories ?? (cr as any).items ?? []) as any[]) : [];
    const prArr = pr ? (((pr as any).packages ?? (pr as any).items ?? []) as any[]) : [];
    const services = srArr.length ? srArr : state.services;
    const categories = crArr.length ? crArr.map(catFromDto) : state.categories;
    const packages = prArr.length ? prArr.map(pkgFromDto) : state.packages;
    if (srArr.length || crArr.length || prArr.length) {
      state = { services, categories, packages };
      emit();
    }
  } catch (e) { console.warn("Services hydrate failed, keeping in-memory fallback:", e); }
}
hydrate(); // no-op until signed in (§2)
// Tenant-switch handling (docket 2026-07-27, item 1): this store had NONE —
// tenant A's services/categories/packages stayed resident under tenant B's
// chrome after a switch. Purge immediately (`_hydrated = true` BLOCKS any
// incidental hydrate() call during the pending window, mirroring the guard
// hydrate() already checks); reload only once the new tenant is confirmed.
onTenantSwitching(() => { state = { services: [], categories: [], packages: [] }; _hydrated = true; emit(); });
// Capability gate (docket 2026-07-31): this re-hydrate is EAGER — it runs for
// every signed-in persona because screens.tsx imports this module, so an Event
// Manager was issuing listServices + listServiceCategories + listStudioPackages
// (3 of the 11 prefetch 403s) without ever opening a catalog screen. The flag
// still resets unconditionally so a later demand-driven subscribe() — the path
// the Services screens actually use, itself route-guarded on services.read —
// refetches normally for whoever does have the capability.
onTenantChange(() => { _hydrated = false; if (can("services.read")) hydrate(); });

/* ── The strict catalogue write contract ─────────────────────────────────
 * StudioServiceCreateInput / StudioServiceUpdateInput accept exactly these
 * top-level keys and reject anything else with a 400. The UI record is a
 * superset — `id`, `duration`, `updated`, `staffRoles`, `resources`,
 * `audience`, `addons` and the top-level image aliases are ITS OWN bookkeeping
 * — so the record is PROJECTED onto the contract rather than posted whole. */
const STUDIO_SERVICE_INPUT_KEYS = [
  "name", "description", "category", "group", "method", "durationMinutes",
  "priceMinor", "currency", "mode", "capacity", "bookings", "staff", "payment",
  "reference", "requiredDocs", "minAdvance", "maxAdvance", "disableBefore",
  "disableAfter", "questions", "disabled", "requiresPerson", "methods",
  "methodConfig", "staffGroups", "staffing", "resourcing", "concurrency",
  "locScope", "prereqs", "tags", "feedback", "visibility", "confirmation",
  "channels", "status", "landing", "exceptions",
] as const;

/** `landing` as the contract takes it. The image travels ONLY nested on the
 *  hero: the confirmed asset id from the UI record is mapped here, and the
 *  top-level aliases are dropped. Detaching sends `image: ""` WITH
 *  `imageAssetId: null` so the stored pair is cleared rather than left pointing
 *  at an image the service no longer shows. */
function studioLandingInput(s: Service): Record<string, unknown> {
  const landing = (s.landing || {}) as Record<string, unknown>;
  const hero: Record<string, unknown> = { ...((landing.hero || {}) as Record<string, unknown>) };
  const image = durableServiceImageUrl(hero.image);
  hero.image = image;
  hero.imageAssetId = image ? serviceImageAssetIdOf(s) : null;
  delete hero.image_asset_id; // UI-only mirror; not a contract key
  return { ...landing, hero };
}

/** The UI record projected onto the strict input. Exported so a test can pin
 *  the request SHAPE, which is the thing that actually broke.
 *
 *  Pricing is deliberately NOT derived here. The editor's price lives at
 *  `methodConfig[method].pricing.price` and already travels inside
 *  `methodConfig`, which is itself a contract key; `priceMinor`/`currency` are
 *  passed through only when the record already carries them. Synthesising a
 *  minor-unit value from the per-method price would be a new monetary rule this
 *  module has never applied. */
export function toStudioServiceInput(s: Service): Record<string, unknown> {
  const src = s as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of STUDIO_SERVICE_INPUT_KEYS) if (src[k] !== undefined) out[k] = src[k];
  /* The UI's summary `duration` is the EDITED truth — svcSyncSummary re-derives
     it from the first method's config on every save. A row read back from the
     server also carries `durationMinutes`, which ensureServiceShape passes
     through untouched (`...s`), so it can be STALE and must never shadow an
     edit. Prefer `duration`; fall back only when it is absent or unusable. */
  const edited = Number(src.duration);
  const carried = Number(src.durationMinutes);
  if (Number.isFinite(edited) && edited > 0) out.durationMinutes = edited;
  else if (Number.isFinite(carried) && carried > 0) out.durationMinutes = carried;
  else delete out.durationMinutes;
  /* A create with no currency SUCCEEDS, and the canonical row comes back with
     `currency: ""` — unconfigured, not invalid. The allowlist copy above takes
     it verbatim (`"" !== undefined`), and the write schema types currency as an
     OPTIONAL exactly-three-character code: absent is accepted, empty is refused
     with "String must contain exactly 3 characters". So the very next save of a
     service the tenant created without a currency is rejected, which is the
     create-then-edit failure this fixes.

     Absent and unconfigured are the same fact to this contract, so an empty
     value is OMITTED rather than sent. A configured code is passed through as
     the tenant set it. Nothing is defaulted: writing USD here would persist a
     currency nobody chose, and the server already stores "" quite happily —
     omitting on update leaves that untouched rather than changing it. */
  const currency = typeof src.currency === "string" ? src.currency.trim() : "";
  if (currency.length === 3) out.currency = currency;
  else delete out.currency;
  out.landing = studioLandingInput(s);
  return out;
}

export const ServicesStore = {
  subscribe(fn: () => void) { hydrate(); subs.add(fn); return () => { subs.delete(fn); }; },

  /* ── services ── */
  services: () => state.services.slice(),
  servicesIn: (catId: string | null) => state.services.filter((s) => s.category === catId),
  getService: (id: string) => state.services.find((s) => s.id === id) || null,
  async upsertService(s: Service) {
    // UIR-12 (2026-07-24): awaits the persist op BEFORE committing local state —
    // no-fake-interactivity mandate. Failure is logged AND rethrown so awaiting
    // callers get a real error state; callers that don't await keep the same
    // console signal as before (just via an unhandled-rejection warning instead
    // of a caught .catch()) — no behavior change for those uncited call sites.
    const rec = svcSyncSummary({ ...s });
    const isNew = !state.services.some((x) => x.id === rec.id);
    const i = state.services.findIndex((x) => x.id === rec.id);
    /* Projected onto the strict contract. Posting the record verbatim is what
       failed a save whose image had uploaded and confirmed perfectly well. */
    const input = toStudioServiceInput(rec);
    let saved: Service = rec;
    try {
      const res = isNew
        ? (typeof createStudioService === "function" ? await createStudioService(input as any, REQUEST_OPTS) : null)
        : (typeof updateStudioService === "function" ? await updateStudioService(rec.id, input as any, REQUEST_OPTS) : null);
      /* Adopt the server's own row. On CREATE it carries the generated id and
         the local row MUST take it — keeping the temporary id would make the
         next save POST a second service instead of updating this one. */
      const row = res && (res as { service?: unknown }).service;
      if (row && typeof row === "object") saved = ensureServiceShape({ ...(row as Record<string, unknown>) });
    } catch (e) {
      console.warn(isNew ? "createStudioService failed:" : "updateStudioService failed:", e);
      throw e;
    }
    logUpsert("service", isNew ? null : (state.services[i] as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: (saved as { name?: string }).name });
    const next: Service = { ...saved, updated: "Just now" };
    if (i >= 0) state.services[i] = next;
    else state.services = [next, ...state.services];
    emit();
    return next;
  },
  async duplicateService(id: string) {
    const src = state.services.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("svc"), name: src.name + " (copy)", status: "draft", bookings: 0, updated: "Just now" };
    if (typeof duplicateStudioService === "function") {
      try { await duplicateStudioService(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateStudioService failed:", e); throw e; }
    }
    const i = state.services.findIndex((x) => x.id === id);
    state.services.splice(i + 1, 0, copy); emit();
    return copy;
  },
  setServiceStatus: async (id: string, status: string) => {
    // Addendum item 16: await-before-commit (UIR-12 pattern at every mutation).
    if (typeof updateStudioService === "function") await updateStudioService(id, { status } as any, REQUEST_OPTS);
    const s = state.services.find((x) => x.id === id); if (s) { s.status = status; s.updated = "Just now"; emit(); }
  },
  removeService: async (id: string) => {
    if (typeof deleteStudioService === "function") await deleteStudioService(id, REQUEST_OPTS);
    const prev = state.services.find((x) => x.id === id);
    if (prev) logDelete("service", prev as unknown as Record<string, unknown>, { name: (prev as { name?: string }).name });
    state.services = state.services.filter((x) => x.id !== id); emit();
  },
  newService: (): Service => ensureServiceShape({ id: _svcId("svc"), name: "", category: "", channels: ["appointments"], methods: ["in-person"], mode: "in-person", duration: 30, capacity: 1, staff: 1, bookings: 0, status: "draft", updated: "Just now" }),
  countInCategory: (catId: string) => state.services.filter((s) => s.category === catId).length,
  /* Move a service to a category, inserting before `beforeId` (or at end). */
  moveService: async (serviceId: string, toCategoryId: string, beforeId: string | null = null) => {
    const arr = state.services;
    const from = arr.findIndex((s) => s.id === serviceId); if (from < 0) return;
    // Addendum item 16: persist first; reorder locally only on ack.
    if (typeof moveStudioService === "function") await moveStudioService({ serviceId, toCategoryId, beforeServiceId: beforeId }, REQUEST_OPTS);
    const [svc] = arr.splice(from, 1);
    svc.category = toCategoryId; svc.updated = "Just now";
    let insertAt: number;
    if (beforeId) { insertAt = arr.findIndex((s) => s.id === beforeId); if (insertAt < 0) insertAt = arr.length; }
    else { let last = -1; arr.forEach((s, i) => { if (s.category === toCategoryId) last = i; }); insertAt = last + 1; }
    arr.splice(insertAt, 0, svc); emit();
  },

  /* ── categories ── */
  categories: () => state.categories.slice(),
  getCategory: (id: string) => state.categories.find((c) => c.id === id) || null,
  async upsertCategory(c: SvcCategory) {
    const isNew = !state.categories.some((x) => x.id === c.id);
    // Addendum item 16: await-before-commit.
    if (isNew && typeof createServiceCategory === "function") await createServiceCategory(c as any, REQUEST_OPTS);
    else if (!isNew && typeof updateServiceCategory === "function") await updateServiceCategory(c.id, c as any, REQUEST_OPTS);
    const i = state.categories.findIndex((x) => x.id === c.id);
    logUpsert("serviceCategory", isNew ? null : (state.categories[i] as unknown as Record<string, unknown>), c as unknown as Record<string, unknown>, { name: c.name });
    if (i >= 0) state.categories[i] = { ...c, updated: "Just now" };
    else state.categories = [...state.categories, { ...c, updated: "Just now" }];
    emit();
    return c;
  },
  async duplicateCategory(id: string) {
    const src = state.categories.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("cat"), name: src.name + " (copy)", status: "draft", updated: "Just now" };
    if (typeof duplicateServiceCategory === "function") {
      try { await duplicateServiceCategory(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateServiceCategory failed:", e); throw e; }
    }
    state.categories = [...state.categories, copy]; emit();
    return copy;
  },
  setCategoryStatus: async (id: string, status: string) => {
    if (typeof updateServiceCategory === "function") await updateServiceCategory(id, { status } as any, REQUEST_OPTS);
    const c = state.categories.find((x) => x.id === id); if (c) { c.status = status; c.updated = "Just now"; emit(); }
  },
  removeCategory: async (id: string) => {
    if (typeof deleteServiceCategory === "function") await deleteServiceCategory(id, REQUEST_OPTS);
    state.categories = state.categories.filter((x) => x.id !== id);
    state.services.forEach((s) => { if (s.category === id) s.category = null; });
    emit();
  },
  newCategory: (): SvcCategory => ({ id: _svcId("cat"), name: "", description: "", hue: "#5142C7", status: "draft", updated: "Just now" }),
  reorderCategory(id: string, dir: "up" | "down") {
    const i = state.categories.findIndex((c) => c.id === id); if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1; if (j < 0 || j >= state.categories.length) return;
    const arr = state.categories; [arr[i], arr[j]] = [arr[j], arr[i]]; emit();
    if (typeof reorderServiceCategories === "function") reorderServiceCategories({ categoryIds: state.categories.map((c) => c.id) }, REQUEST_OPTS).catch((e: unknown) => console.warn("reorderServiceCategories failed:", e));
  },

  /* ── packages ── */
  packages: () => state.packages.slice(),
  getPackage: (id: string) => state.packages.find((p) => p.id === id) || null,
  async upsertPackage(p: SvcPackage) {
    const isNew = !state.packages.some((x) => x.id === p.id);
    const i = state.packages.findIndex((x) => x.id === p.id);
    const clean = { ...p, items: (p.items || []).map((i2) => ({ ...i2 })), updated: "Just now" };
    // Addendum item 16 (final residue): await-before-commit.
    if (isNew && typeof createStudioPackage === "function") await createStudioPackage(clean as any, REQUEST_OPTS);
    else if (!isNew && typeof updateStudioPackage === "function") await updateStudioPackage(clean.id, clean as any, REQUEST_OPTS);
    if (i >= 0) state.packages[i] = clean;
    else state.packages = [clean, ...state.packages];
    emit();
    return clean;
  },
  async duplicatePackage(id: string) {
    const src = state.packages.find((x) => x.id === id); if (!src) return null;
    const copy = { ...src, id: _svcId("pkg"), name: src.name + " (copy)", status: "draft", updated: "Just now", items: src.items.map((i) => ({ ...i })) };
    if (typeof duplicateStudioPackage === "function") {
      try { await duplicateStudioPackage(id, REQUEST_OPTS); }
      catch (e) { console.warn("duplicateStudioPackage failed:", e); throw e; }
    }
    state.packages = [copy, ...state.packages]; emit();
    return copy;
  },
  setPackageStatus: async (id: string, status: string) => {
    if (typeof updateStudioPackage === "function") await updateStudioPackage(id, { status } as any, REQUEST_OPTS);
    const p = state.packages.find((x) => x.id === id); if (p) { p.status = status; p.updated = "Just now"; emit(); }
  },
  removePackage: async (id: string) => {
    if (typeof deleteStudioPackage === "function") await deleteStudioPackage(id, REQUEST_OPTS);
    state.packages = state.packages.filter((x) => x.id !== id); emit();
  },
  newPackage: (): SvcPackage => ({ id: _svcId("pkg"), name: "", description: "", status: "draft", hue: "#5142C7", items: [], updated: "Just now" }),
  /* Count of distinct services a package resolves to. */
  packageServiceCount(pkg: SvcPackage) {
    const ids = new Set<string>();
    (pkg.items || []).forEach((it) => {
      if (it.type === "category") state.services.filter((s) => s.category === it.id).forEach((s) => ids.add(s.id));
      else ids.add(it.id);
    });
    return ids.size;
  },
  /* Reorder packages in the library list (docket 2026-07-28: reorderStudioPackages
   * was imported but never called). Mirrors reorderCategory's optimistic-then-persist shape. */
  reorderPackage(id: string, dir: "up" | "down") {
    const i = state.packages.findIndex((p) => p.id === id); if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1; if (j < 0 || j >= state.packages.length) return;
    const arr = state.packages; [arr[i], arr[j]] = [arr[j], arr[i]]; emit();
    if (typeof reorderStudioPackages === "function") reorderStudioPackages({ packageIds: state.packages.map((p) => p.id) }, REQUEST_OPTS).catch((e: unknown) => console.warn("reorderStudioPackages failed:", e));
  },
};

export function useServices() {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => ServicesStore.subscribe(force), []);
  return ServicesStore;
}

/* Resolvers used across the screens. */
export const svcCategoryName = (id?: string | null) => !id ? "None" : ((ServicesStore.getCategory(id) || ({} as SvcCategory)).name || "Uncategorised");
export const svcCategoryHue  = (id?: string | null) => (id && ServicesStore.getCategory(id) ? ServicesStore.getCategory(id)!.hue : "#94A3B8");
export const svcDurationLabel = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`;

/* ═══ Service image — the typed SERVICE asset lifecycle ══════════════════════
 *
 *   presign (presignStudioServiceImage) → upload (uploadStudioServiceImage) →
 *   confirm (confirmStudioServiceImage) → the service stores the confirmed URL
 *   on `landing.hero.image` and the asset id on `imageAssetId`.
 *
 * All three are the service-specific platform operations — no generic canvas
 * call and no raw fetch anywhere in this pipeline. The service is the asset's
 * owner, so the presign is scoped to it and the confirm reports whether the
 * server deduplicated the upload onto an asset that already existed.
 *
 * Where the dimensions go: `width`/`height` are a CONFIRM-time concern, not a
 * presign one. The presign body is exactly what its schema names — `filename`,
 * `mime_type`, `byte_size`, and an optional `alt_text` — so nothing extra is
 * sent to an operation that did not ask for it.
 *
 * Accepted types are PNG and JPEG, and only those: the service API refuses
 * GIF, WebP, AVIF and SVG. They are refused HERE too, before a presign is ever
 * issued for an asset that could never be confirmed — and note that the
 * downscale step must therefore re-encode in the source type rather than WebP
 * (see prepareServiceImage in service-editor-controls.tsx).
 *
 * SIZE: the ceiling is 5 MB (SERVICE_IMAGE_MAX_BYTES), enforced by the backend
 * on BOTH presign and the actual PUT. It is checked here against the ORIGINAL
 * file — before any resize, re-encode or presign — so an oversized pick costs
 * zero network requests. A server-reported `maxBytes` may LOWER the effective
 * limit but never raise it above the ceiling.
 *
 * ⛔ NOTHING HERE DELETES AN ASSET. A confirmed asset can be referenced by more
 * than one record — `deduplicated` / `duplicateOf` says so explicitly, and a
 * saved service keeps its reference after the editor closes. So detaching an
 * image from a service CLEARS THE REFERENCE (`image`, `imageAssetId` and
 * `image_asset_id`) and leaves the asset in place. Reaping genuinely
 * unreferenced assets is the platform's job; deleting from here is how an image
 * vanishes from a record nobody was editing.
 *
 * The screen only ever receives a CONFIRMED asset. A `blob:` / `data:` preview
 * is never a storable value, and a pasted URL is an explicit override that
 * carries no asset id — so a stale object URL can never outrank either.
 */

/* The operations answer in either camelCase or snake_case depending on the
 * deployment's serializer, so every field read goes through these. A wrong
 * guess here reads as "the server sent nothing", which is exactly the failure
 * that is indistinguishable from a real empty response — so both spellings are
 * always tried rather than assumed. (Read-side only: the REQUEST bodies below
 * are single-spelled, exactly as each operation's schema names them.) */
function assetField(src: Record<string, any> | null | undefined, ...keys: string[]): unknown {
  if (!src) return undefined;
  for (const k of keys) {
    const v = src[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}
function assetString(src: Record<string, any> | null | undefined, ...keys: string[]): string {
  const v = assetField(src, ...keys);
  return typeof v === "string" ? v.trim() : "";
}

export type ServiceImageStage = "queued" | "preparing" | "uploading" | "confirming" | "saved" | "failed";
export interface ServiceImageUploadState { stage: ServiceImageStage | "idle"; progress?: number; error: string; file: File | null }
export const SERVICE_IMAGE_IDLE: ServiceImageUploadState = { stage: "idle", error: "", file: null };
export const serviceImageBusy = (u: ServiceImageUploadState) =>
  u.stage === "queued" || u.stage === "preparing" || u.stage === "uploading" || u.stage === "confirming";

/** The only image types the service API accepts. GIF, WebP, AVIF and SVG are
 *  refused server-side, so the editor refuses them first — the pick never
 *  reaches presign. This list is also the `accept` the upload field mounts. */
export const SERVICE_IMAGE_TYPES = ["image/png", "image/jpeg"]; // ui-config
export const SERVICE_IMAGE_ACCEPT = SERVICE_IMAGE_TYPES.join(",");

/** The hard ceiling for a service image: 5 MB. The backend enforces it on the
 *  presign AND on the actual PUT; this is the same number, stated up front in
 *  the field's hint and checked before a single request is made. */
export const SERVICE_IMAGE_MAX_BYTES = 5_242_880;

/* Seeded with the ceiling — not null — so the field states the limit BEFORE the
   first upload and can refuse an oversized pick with no presign. A server
   `maxBytes` narrows this (Math.min) and can never widen it. */
let serviceImageMaxBytes: number = SERVICE_IMAGE_MAX_BYTES;
/** The effective limit in bytes: the 5 MB ceiling, or a stricter server number. */
export const serviceImageSizeLimit = (): number => serviceImageMaxBytes;
export const serviceImageBytesLabel = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** "" when the type is accepted, otherwise the reason to show. */
export function serviceImageTypeError(file: { type?: string }): string {
  return SERVICE_IMAGE_TYPES.includes(String(file.type || "").toLowerCase())
    ? ""
    : i18nT("services.data.imageTypeUnsupported", "Choose a PNG or JPEG image.");
}

/**
 * "" when the file is within the limit, otherwise the reason to show.
 *
 * Takes the ORIGINAL picked file, never a prepared blob: the downscale can make
 * an over-limit file fit, and accepting it on that basis would upload something
 * the author never chose. Exactly the limit is allowed (`>` not `>=`).
 */
export function serviceImageSizeError(file: { size?: number }): string {
  const size = Number(file && file.size);
  return Number.isFinite(size) && size > serviceImageMaxBytes
    ? i18nT("services.data.imageTooLarge", "Image must be 5 MB or smaller.")
    : "";
}

/** A backend size refusal, however it surfaces: an HTTP 413, or a validation
 *  message naming the limit. Mapped to the same sentence the picker shows so
 *  the author never sees two different words for one rule. */
function isPayloadTooLarge(e: unknown): boolean {
  const err = e as { status?: number; statusCode?: number; response?: { status?: number }; message?: string };
  const status = Number(err?.status ?? err?.statusCode ?? err?.response?.status);
  if (status === 413) return true;
  return /(^|\D)413(\D|$)|payload too large|request entity too large|file too large|image too large|max_bytes|maxbytes/i.test(String(err?.message || ""));
}

/** A value a service may store as its image: an http(s) or root-relative URL. Never `blob:` / `data:`. */
export function durableServiceImageUrl(value: unknown): string {
  const v = typeof value === "string" ? value.trim() : "";
  return v && (/^https?:\/\//i.test(v) || /^\/(?!\/)/.test(v)) ? v : "";
}

/** The image a service presents (list cards and rows, journeys), or "". */
export function serviceImageOf(s: Service | null | undefined): string {
  return durableServiceImageUrl(s && s.landing && s.landing.hero && s.landing.hero.image);
}

/** The confirmed asset behind the service image, or null (no image, or a pasted URL). */
export function serviceImageAssetIdOf(s: Service | null | undefined): string | null {
  if (!s || !serviceImageOf(s)) return null;
  const hero = (s.landing && s.landing.hero) || {};
  const id = assetString(s as Record<string, any>, "imageAssetId", "image_asset_id")
    || assetString(hero as Record<string, any>, "imageAssetId", "image_asset_id");
  return id || null;
}

/**
 * The service with its image set. An asset id is kept only beside a durable
 * URL; detaching writes `null` to every spelling rather than dropping the key,
 * so a merge-style update CLEARS the stored reference instead of preserving a
 * pointer to an image this service no longer shows.
 */
export function withServiceImage(s: Service, url: string, assetId: string | null): Service {
  const image = durableServiceImageUrl(url);
  const keptId = image && assetId ? assetId : null;
  /* Mirrored onto the hero as well as the top level, in both spellings: the
     catalog write schema passes unknown hero keys through, so the id survives a
     round trip there even where a top-level `imageAssetId` is not yet part of
     the contract, and a record stored under either spelling is cleared on
     detach rather than half-cleared. */
  const hero: Record<string, any> = { ...((s.landing && s.landing.hero) || {}), image, imageAssetId: keptId, image_asset_id: keptId };
  return { ...s, imageAssetId: keptId, image_asset_id: keptId, landing: { ...(s.landing || {}), hero } };
}

/**
 * Journey presentation precedence: the journey's explicit override, then the
 * catalog service's confirmed image, then no image. `show` is the journey's
 * `showServiceImages` setting — off means no image at all.
 */
export function resolveServiceImage(opts: { show: boolean; override?: unknown; catalogImage?: unknown }): string {
  if (!opts.show) return "";
  return durableServiceImageUrl(opts.override) || durableServiceImageUrl(opts.catalogImage);
}

export interface ConfirmedServiceImage {
  assetId: string;
  url: string;
  /** The server matched an existing asset (`deduplicated`), so `assetId` is the
   *  shared original it pointed at. Reported for the record — no caller deletes
   *  a confirmed asset, shared or not. */
  shared: boolean;
}

/**
 * Upload one prepared image and return the confirmed asset. Throws a
 * user-actionable message on any failure — the caller must not treat a throw as
 * saved. A failed or abandoned attempt leaves whatever the server created in
 * place: an unconfirmed pending asset is the platform's to reap, and a
 * confirmed one is never this editor's to delete.
 *
 * `width` / `height` are carried for the CONFIRM call; they are deliberately
 * not part of the presign body. `originalSize` is the picked file's own size,
 * so this facade re-checks the 5 MB ceiling against what the author actually
 * chose rather than only the downscaled blob.
 */
export async function uploadServiceImage(input: {
  blob: Blob;
  filename: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  originalSize?: number | null;
  altText?: string | null;
  serviceId?: string | null;
  signal?: AbortSignal;
  onStage?: (stage: ServiceImageStage, progress?: number) => void;
}): Promise<ConfirmedServiceImage> {
  const stage = input.onStage || (() => {});
  const typeError = serviceImageTypeError({ type: input.mimeType });
  if (typeError) throw new Error(typeError);
  const aborted = () => !!(input.signal && input.signal.aborted);
  const tooLarge = () => new Error(i18nT("services.data.imageTooLarge", "Image must be 5 MB or smaller."));

  /* Facade guard, mirroring the picker's: the ORIGINAL file when the caller
     supplied its size, and the prepared blob either way. Both are checked
     BEFORE the presign, so an oversized image issues no request at all. */
  const originalSizeError = serviceImageSizeError({ size: Number(input.originalSize) });
  if (originalSizeError) throw new Error(originalSizeError);
  if (input.blob.size > serviceImageMaxBytes) throw tooLarge();

  /* Exactly the fields the presign schema names — nothing more. `alt_text` is
     the one optional member, sent only when there is a value to send. */
  const altText = typeof input.altText === "string" ? input.altText.trim() : "";
  const presignBody: Record<string, unknown> = {
    filename: input.filename,
    mime_type: input.mimeType,
    byte_size: input.blob.size,
    ...(altText ? { alt_text: altText } : {}),
  };

  let presigned: Record<string, any>;
  try {
    presigned = (await presignStudioServiceImage(presignBody as any, REQUEST_OPTS)) as Record<string, any>;
  } catch (e) {
    console.warn("presignStudioServiceImage failed:", e);
    /* A size refusal at presign says the same thing the picker would have — say
       it in the same words rather than a generic "couldn't be started". */
    if (isPayloadTooLarge(e)) throw tooLarge();
    throw new Error(i18nT("services.data.imagePresignFailed", "The upload couldn’t be started. Retry in a moment."));
  }

  /* A server limit NARROWS the ceiling and can never widen it. */
  const reportedMax = Number(assetField(presigned, "maxBytes", "max_bytes"));
  if (Number.isFinite(reportedMax) && reportedMax > 0) {
    serviceImageMaxBytes = Math.min(reportedMax, SERVICE_IMAGE_MAX_BYTES);
  }
  if (input.blob.size > serviceImageMaxBytes) throw tooLarge();

  const pendingAssetId = assetString(presigned, "assetId", "asset_id");
  const uploadUrl = assetString(presigned, "uploadUrl", "upload_url");
  /* A presign that answered without a target is a failed presign, not an empty
     one — never fall through to an upload that would PUT to "". */
  if (!pendingAssetId || !uploadUrl) {
    console.warn("presignStudioServiceImage returned no upload target");
    throw new Error(i18nT("services.data.imagePresignFailed", "The upload couldn’t be started. Retry in a moment."));
  }
  if (aborted()) throw new Error("aborted");

  stage("uploading");
  try {
    await uploadStudioServiceImage(uploadUrl, input.blob, { signal: input.signal });
  } catch (e) {
    console.warn("uploadStudioServiceImage failed:", e);
    /* The PUT enforces the limit independently of the presign, so a 413 here is
       the expected shape of an over-limit upload that slipped both guards. */
    if (isPayloadTooLarge(e)) throw tooLarge();
    throw new Error(i18nT("services.data.imageUploadRejected", "The image upload was rejected. Retry, or choose a different image."));
  }
  if (aborted()) throw new Error("aborted");

  stage("confirming");
  let confirmed: Record<string, any>;
  try {
    confirmed = (await confirmStudioServiceImage(pendingAssetId, { width: input.width, height: input.height } as any, REQUEST_OPTS)) as Record<string, any>;
  } catch (e) {
    console.warn("confirmStudioServiceImage failed:", e);
    if (isPayloadTooLarge(e)) throw tooLarge();
    throw new Error(i18nT("services.data.imageConfirmFailed", "The image uploaded but couldn’t be confirmed. Retry in a moment."));
  }

  const shared = !!assetField(confirmed, "deduplicated");
  const duplicateOf = assetString(confirmed, "duplicateOf", "duplicate_of");
  const assetId = (shared && duplicateOf) || assetString(confirmed, "assetId", "asset_id") || pendingAssetId;
  const url = durableServiceImageUrl(assetField(confirmed, "imageUrl", "image_url"));
  if (!url) {
    throw new Error(i18nT("services.data.imageNoUrl", "The image was stored without a usable address. Retry in a moment."));
  }
  stage("saved", 1);
  return { assetId, url, shared };
}
